import { useEffect, useState, useRef } from "react";
import gql from "graphql-tag";
import { Grid2 as Grid, Button, Alert, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, InputAdornment, Tooltip, TextField as MuiTextField, IconButton } from "@mui/material";
import { Formik, Form, Field } from "formik";
import { TextField } from "formik-material-ui";
import { useMask, format, unformat } from "@react-input/mask";
import CancelConsent from "./ConsentCancel";
import useConsentStore, { EmailVerifiedState } from "../../store/consentStore";
import { useMutation } from "@apollo/client";
import ForbiddenDomainList from "../../utils/ForbiddenDomainList";
import { useSearchParams } from "react-router-dom";
import { DateTime } from 'luxon';
import UnpublishedOutlinedIcon from '@mui/icons-material/UnpublishedOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import useUserStore from "../../store/userStore";
import { useCompanySelection } from "../../hooks/useCompanySelection";
import { enqueueSnackbar } from "notistack";


interface ConsentFormValuesType {
  phone: string;
  email: string;
}

interface ConsentFormErrorsType {
  phone?: string;
  email?: string;
}

const UPDATE_CONSENT_ONLINE_CONTACT_INFO = gql`
  mutation UpdateConsentOnlineContactInfo($email: String, $phone: String, $textId: Int, $ico: String!) {
    updateConsentOnlineContactInfo(email: $email, phone: $phone, textId: $textId, ico: $ico) {
      id
      email
      phone
      ico
      userId
      createdById
      text {
        id
        text
        type
        validTo
      }
    }
  }
`;

const CONFIRM_EMAIL_CONSENT_ONLINE = gql`
  mutation confirmEmailConsentOnline($emailVerifyCode: String!, $ico: String!) {
    confirmEmailConsentOnline(emailVerifyCode: $emailVerifyCode, ico: $ico)
  }
`;

const RESEND_EMAIL_VERIFY_CODE = gql`
  mutation resendVerificationEmailConsentOnline($ico: String!) {
    resendVerificationEmailConsentOnline(ico: $ico)
  }
`;

const ConsentForm = () => {
  const {
    setConsentDialogOpen,
    getSelectedConsentOnline,
  } = useConsentStore();

  const {
    user,
  } = useUserStore();

  const { selectedIco } = useCompanySelection();
  const [searchParams, setSearchParams] = useSearchParams();

  // Use data from selected company
  const selectedConsentOnline = getSelectedConsentOnline();
  const currentEmail = selectedConsentOnline?.email || '';
  const currentPhone = selectedConsentOnline?.phone || '';
  const currentConsentOnlineText = selectedConsentOnline?.text;
  const currentConsentOnlineCreatedById = selectedConsentOnline?.createdById;
  const currentEmailVerifyCodeExpires = selectedConsentOnline?.emailVerifyCodeExpires;
  const currentEmailVerifiedState = selectedConsentOnline?.emailVerifiedState;

  const [confirmEmailConsentOnline] = useMutation(CONFIRM_EMAIL_CONSENT_ONLINE, {
    refetchQueries: ["consentsOnline"],
  });

  const [resendVerificationEmailConsentOnline, {
    loading: resendVerificationEmailConsentOnlineLoading
  }] = useMutation(RESEND_EMAIL_VERIFY_CODE, {
    refetchQueries: ["consentsOnline"],
  });

  const [verificationError, setVerificationError] = useState<string | null>(null);
  const [verificationSuccess, setVerificationSuccess] = useState<boolean>(false);
  const [copySuccess, setCopySuccess] = useState<boolean>(false);

  const removeVerifyEmailFromUrl = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('verifyEmail');
    setSearchParams(newParams, { replace: true });
  };

  const handleCloseVerificationError = () => {
    setVerificationError(null);
    removeVerifyEmailFromUrl();
  };

  const handleCloseVerificationSuccess = () => {
    setVerificationSuccess(false);
    removeVerifyEmailFromUrl();
  };

  const handleCopySignCode = async () => {
    try {
      await navigator.clipboard.writeText(user?.signCode || '');
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000); // Hide success message after 2 seconds
    } catch (error) {
      console.error('Failed to copy sign code:', error);
    }
  };

  const [isFormSubmitted, setIsFormSubmitted] = useState<boolean>(false);
  const resetFormRef = useRef<((options?: { values?: ConsentFormValuesType }) => void) | null>(null);
  const lastSubmittedValuesRef = useRef<{ email: string; phone: string } | null>(null);

  const phoneMaskOptions = {
    mask: '+(___) ___-___-___',
    replacement: { _: /\d/ },
    showMask: true,
  }

  const inputPhoneRef = useMask(phoneMaskOptions);
  const defaultPhoneValue = format(currentPhone, phoneMaskOptions);

  const validation = (values: ConsentFormValuesType) => {
    const errors: ConsentFormErrorsType = {};
    const requiredMsg = "Pole musí být vyplněno";
    const errorPhoneMessage = "Neplatné telefonní číslo";
    const unformatedPhone = unformat(values.phone, phoneMaskOptions);

    const forbiddenDomains = ForbiddenDomainList;

    if (!values.email) {
      errors.email = requiredMsg;
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(values.email)) {
      errors.email = "Neplatný email";
    } else {
      const emailDomain = values.email.split("@")[1]?.toLowerCase();
      if (forbiddenDomains.includes(emailDomain)) {
        errors.email = "Musíte použít soukromý email";
      }
    }

    if (!unformatedPhone || unformatedPhone.length < 12) {
      errors.phone = requiredMsg;
    } else {
      const countryCode = unformatedPhone.slice(0, 3);
      if (countryCode === "420") {
        if (!/^420[67]\d{8}$/.test(unformatedPhone)) {
          errors.phone = errorPhoneMessage;
        }
      } else if (countryCode === "421") {
        if (!/^4219\d{8}$/.test(unformatedPhone)) {
          errors.phone = errorPhoneMessage;
        }
      } else {
        errors.phone = errorPhoneMessage;
      }
    }

    return errors;
  };

  const [updateConsentOnlineContactInfo] = useMutation(UPDATE_CONSENT_ONLINE_CONTACT_INFO, {
    refetchQueries: "all",
  });

  const handleUpdateContactInfo = async ({
    email,
    phone,
    textId,
    ico,
  }: {
    email: string;
    phone: string;
    textId: number | null;
    ico: string;
  }) => {
    await updateConsentOnlineContactInfo({
      variables: {
        email,
        phone,
        textId,
        ico,
      },
    });
  };

  const handleFormSubmit = async (
    values: ConsentFormValuesType,
    setSubmitting: {
      (isSubmitting: boolean): void;
    },
    resetForm?: (options?: { values?: ConsentFormValuesType }) => void
  ) => {
    const { phone: _phone, email: _email } = values;
    const unformatedPhone = unformat(_phone, phoneMaskOptions);

    if (!selectedIco) {
      enqueueSnackbar("Chybí identifikace společnosti", { variant: "error" });
      return;
    }

    setIsFormSubmitted(false);

    try {
      setSubmitting(false);
      setIsFormSubmitted(true);

      await handleUpdateContactInfo({
        email: _email,
        phone: `+${unformatedPhone}`,
        textId: currentConsentOnlineText?.id ?? null,
        ico: selectedIco,
      });

      setIsFormSubmitted(true);

      // Store resetForm function and submitted values for later use
      // Form will be reset when data is updated in store via useEffect
      if (resetForm) {
        resetFormRef.current = resetForm;
        lastSubmittedValuesRef.current = {
          email: _email,
          phone: _phone,
        };
      }

      if (!currentEmail && !currentPhone) {
        setConsentDialogOpen(true);
      }
    } catch (error) {
      console.error("Error updating contact info:", error);
      enqueueSnackbar("Při ukládání nastala chyba", { variant: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  // Email verification from URL param
  const verifyEmailCode = searchParams.get('verifyEmail');

  useEffect(() => {
    if (verifyEmailCode && selectedIco) {
      confirmEmailConsentOnline({ variables: { emailVerifyCode: verifyEmailCode, ico: selectedIco } })
        .then(() => {
          setVerificationSuccess(true);
        })
        .catch((error) => {
          if (error.message.includes('expired')) {
            setVerificationError('Kód pro ověření e-mailu vypršel. Požádej o nový kód pro ověření.');
          } else if (error.message.includes('Invalid')) {
            setVerificationError('Kód pro ověření e-mailu je neplatný.');
          } else {
            setVerificationError('Došlo k chybě při ověřování vašeho e-mailu. Zkus to prosím znovu.');
          }
        });
    }
  }, [confirmEmailConsentOnline, verifyEmailCode, selectedIco]);

  // Reset form when data is updated after submission
  useEffect(() => {
    if (
      resetFormRef.current
      && lastSubmittedValuesRef.current
      && currentEmail === lastSubmittedValuesRef.current.email
      && format(currentPhone, phoneMaskOptions) === lastSubmittedValuesRef.current.phone
    ) {
      resetFormRef.current({
        values: {
          email: currentEmail,
          phone: format(currentPhone, phoneMaskOptions),
        },
      });
      lastSubmittedValuesRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEmail, currentPhone]);

  return (
    <>
      <Dialog
        open={verificationSuccess}
        onClose={handleCloseVerificationSuccess}
        aria-labelledby="verification-success-dialog-title"
      >
        <DialogTitle id="verification-success-dialog-title" color="success">
          E-mail úspěšně ověřen
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Vaše e-mailová adresa byla úspěšně ověřena. Nyní můžete pokračovat v procesu.
          </DialogContentText>
        </DialogContent>
        <DialogActions
          sx={{
            justifyContent: "center",
          }}
        >
          <Button onClick={handleCloseVerificationSuccess} variant="contained" color="primary">
            Pokračovat
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!verificationError}
        onClose={handleCloseVerificationError}
        aria-labelledby="verification-error-dialog-title"
        sx={{
          "& .MuiDialog-paper": {
            padding: "1rem",
          },
        }}
      >
        <DialogTitle id="verification-error-dialog-title" color="error">
          Chyba ověření e-mailu
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {verificationError}
          </DialogContentText>
        </DialogContent>
        <DialogActions
          sx={{
            justifyContent: "center",
          }}
        >
          <Button onClick={handleCloseVerificationError} variant="contained" color="primary">
            Zpět na stránku souhlasu
          </Button>
        </DialogActions>
      </Dialog>

      <Formik
        enableReinitialize
        validate={validation}
        initialValues={{
          email: currentEmail,
          phone: defaultPhoneValue,
        }}
        onSubmit={(values, { setSubmitting, resetForm }) => {
          handleFormSubmit(values, setSubmitting, resetForm);
        }}
      >
        {({ isSubmitting, values }) => (
          <Form>
            <Grid
              container
              maxWidth={700}
              mx="auto"
              mb={2}
            >
              <Grid
                size={{ xs: 12, md: 6 }}
                textAlign={{ xs: "left", md: "right" }}
                px={{
                  xs: 0,
                  md: 1,
                }}
                mt={{
                  xs: 0,
                  md: 2,
                }}
              >
                <label htmlFor="email" style={{
                  fontWeight: "bold",
                }}>Soukromá e-mailová adresa:</label>
              </Grid>

              <Grid
                size={{ xs: 12, md: 6 }}
              >
                <Field
                  style={{
                    marginBottom: 8,
                  }}
                  slotProps={
                    currentEmail && currentConsentOnlineCreatedById !== 1 ?
                      {
                        input: {
                          endAdornment: (
                            <InputAdornment position="end">
                              <Tooltip
                                title={
                                  currentEmailVerifiedState !== EmailVerifiedState.VERIFIED
                                    ? "Neověřená e-mailová adresa"
                                    : "Ověřená e-mailová adresa"
                                }
                              >
                                {
                                  currentEmailVerifiedState !== EmailVerifiedState.VERIFIED
                                    ? (
                                      <UnpublishedOutlinedIcon color="warning" />
                                    )
                                    : (
                                      <CheckCircleOutlineOutlinedIcon color="success" />
                                    )
                                }
                              </Tooltip>
                            </InputAdornment>
                          ),
                        },
                      } : undefined
                  }
                  id="email"
                  component={TextField}
                  name="email"
                  variant="outlined"
                  size="small"
                  margin="dense"
                  fullWidth
                />
              </Grid>
            </Grid>

            <Grid
              container
              maxWidth={700}
              mx="auto"
              mb={2}
            >
              <Grid
                size={{ xs: 12, md: 6 }}
                textAlign={{ xs: "left", md: "right" }}
                px={{
                  xs: 0,
                  md: 1,
                }}
                mt={{
                  xs: 0,
                  md: 2,
                }}
              >
                <label htmlFor="phone" style={{
                  fontWeight: "bold",
                }}>Telefonní číslo:</label>
              </Grid>

              <Grid
                size={{ xs: 12, md: 6 }}
              >
                <Field
                  style={{
                    marginBottom: 8,
                  }}
                  id="phone"
                  component={TextField}
                  inputRef={inputPhoneRef}
                  placeholder="+(___) ___-___-___"
                  name="phone"
                  type="tel"
                  variant="outlined"
                  size="small"
                  margin="dense"
                  fullWidth
                />
              </Grid>
            </Grid>

            <Grid
              container
              maxWidth={700}
              mx="auto"
              mb={2}
              rowGap={2}
            >
              {isFormSubmitted && (
                <Grid
                  size={12}
                  textAlign="left"
                >
                  <Alert
                    severity="success"
                  >
                    Údaje byly úspěšně uloženy
                  </Alert>
                </Grid>
              )}

              <Grid
                size={{ xs: 12, md: 6 }}
                textAlign="right"
                alignContent="center"
                px={{
                  xs: 0,
                  md: 1,
                }}
              >
                <CancelConsent />
              </Grid>

              <Grid
                size={{ xs: 12, md: 6 }}
                textAlign={{ xs: "right", md: "left" }}
              >

                <Button
                  disabled={
                    isSubmitting
                    || (
                      values.email === currentEmail
                      && values.phone === defaultPhoneValue
                      && !!currentEmail
                      && !!currentPhone
                      && currentEmailVerifiedState === EmailVerifiedState.VERIFIED
                    )
                  }
                  type="submit"
                  variant="contained"
                  disableElevation
                  color="success"
                >
                  Uložit a potvrdit údaje
                </Button>
              </Grid>
            </Grid>

            {
              currentEmailVerifiedState !== EmailVerifiedState.VERIFIED
              && currentEmailVerifyCodeExpires
              && DateTime.fromISO(currentEmailVerifyCodeExpires.toString()).toJSDate() < new Date()
              && (
                <Grid
                  container
                  alignItems="center"
                  width="100%">
                  <Grid
                    size={12}
                    textAlign="center"
                    mb={2}
                  >
                    <Alert
                      severity="warning"
                    >
                      Kód pro ověření e-mailu vypršel. Požádej o nový kód pro ověření.
                    </Alert>
                  </Grid>

                  <Grid
                    size={12}
                    textAlign="center"
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={resendVerificationEmailConsentOnlineLoading || !selectedIco}
                      onClick={() => selectedIco && resendVerificationEmailConsentOnline({ variables: { ico: selectedIco } })}
                    >
                      Poslat nový kód pro ověření e-mailu
                    </Button>
                  </Grid>
                </Grid>
              )}

            {
              currentEmail
              && currentEmailVerifiedState !== EmailVerifiedState.VERIFIED
              && currentEmailVerifyCodeExpires
              && DateTime.fromISO(currentEmailVerifyCodeExpires.toString()).toJSDate() > new Date()
              && (
                <Grid
                  container
                  alignItems="center"
                  width="100%">
                  <Grid
                    size={12}
                    textAlign="center"
                    mb={2}
                  >
                    <Alert
                      severity="info"
                    >
                      {
                        currentEmailVerifiedState === EmailVerifiedState.SUBMITTED && (
                          "Na e-mailovou adresu bude odeslán odkaz pro ověření e-mailu."
                        )
                      }
                      {
                        currentEmailVerifiedState === EmailVerifiedState.SENT && (
                          "Na e-mailovou adresu byl odeslán odkaz pro ověření e-mailu."
                        )
                      }
                    </Alert>
                  </Grid>
                </Grid>
              )
            }
          </Form>
        )}
      </Formik>

      {
        user?.signCode && user?.signCode.length > 0 && (
          <Grid
            container
            maxWidth={700}
            mx="auto"
            mb={2}
          >
            <Grid
              size={{ xs: 12, md: 6 }}
              textAlign={{ xs: "left", md: "right" }}
              px={{
                xs: 0,
                md: 1,
              }}
              mt={{
                xs: 0,
                md: 2,
              }}
            >
              <label htmlFor="signCode" style={{
                fontWeight: "bold",
              }}>Podpisový kód:</label>
            </Grid>

            <Grid
              size={{ xs: 12, md: 6 }}
            >
              <MuiTextField
                id="signCode"
                value={user.signCode}
                disabled
                variant="outlined"
                size="small"
                margin="dense"
                sx={{
                  width: "100%",
                }}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip
                          title={copySuccess ? "Zkopírováno!" : "Kopírovat kód"}
                        >
                          <IconButton
                            onClick={handleCopySignCode}
                            edge="end"
                            size="small"
                            color={copySuccess ? "success" : "default"}
                          >
                            <ContentCopyIcon />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Grid>
          </Grid>
        )
      }
    </>
  );
};

export default ConsentForm;
